import type {
  ClientExceptionEntity,
  GetAllPostsByAppVariantAndSelectedGeoAreasRequest,
  GetMeinOrtPostsRequest,
  GetSagsUnsPostsRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';

import { useGetMeinOrtPosts } from '@/features/api/hooks/grapevine-post/useGetMeinOrtPosts';
import { useGetSagsUnsPosts } from '@/features/api/hooks/grapevine-post/useGetSagsUnsPosts';
import { MeinOrtTab } from '@/features/mein-ort/components/meinOrtTypes';
import { useMeinOrtNewsFeed } from '@/features/mein-ort/hooks/useMeinOrtNewsFeed';
import { LAST_100_DAYS, SIZE } from '@/features/posts/constants/params';
import {
  useGetMeinOrtPostsParams,
  useGetPostsParams
} from '@/features/posts/hooks/useGetPostsParams';
import { usePostSortStore } from '@/features/posts/store/postSortStore';
import { PostFeedType } from '@/features/posts/types/postFeed';
import type { GenericPostDetail } from '@/features/posts/types/posts';

export enum MeinOrtFeedKind {
  NONE = 'none',
  MEIN_ORT = 'meinOrt',
  NEWS = 'news',
  SAGS_UNS = 'sagsUns'
}

const POST_FEED_CONFIG: Partial<
  Record<MeinOrtTab, { kind: MeinOrtFeedKind; feedType: PostFeedType }>
> = {
  [MeinOrtTab.NEWS]: {
    kind: MeinOrtFeedKind.NEWS,
    feedType: PostFeedType.NEWS_ITEM
  },
  [MeinOrtTab.PLAUSCH]: {
    kind: MeinOrtFeedKind.MEIN_ORT,
    feedType: PostFeedType.GOSSIP
  },
  [MeinOrtTab.MARKT]: {
    kind: MeinOrtFeedKind.MEIN_ORT,
    feedType: PostFeedType.MARKET
  },
  [MeinOrtTab.EVENTS]: {
    kind: MeinOrtFeedKind.MEIN_ORT,
    feedType: PostFeedType.HAPPENING
  },
  [MeinOrtTab.SAGSUNS]: {
    kind: MeinOrtFeedKind.SAGS_UNS,
    feedType: PostFeedType.SUGGESTION
  }
};

const DEFAULT_FEED_CONFIG = {
  kind: MeinOrtFeedKind.NONE,
  feedType: PostFeedType.ALL
};

type MeinOrtPostFeedParams =
  | GetAllPostsByAppVariantAndSelectedGeoAreasRequest
  | GetMeinOrtPostsRequest
  | GetSagsUnsPostsRequest;

type MeinOrtPostFeedQuery = UseInfiniteQueryResult<
  GenericPostDetail[],
  ClientExceptionEntity
>;

type MeinOrtPostFeedSelection = {
  params: MeinOrtPostFeedParams;
  query: MeinOrtPostFeedQuery;
};

export type UseMeinOrtPostFeedResult = MeinOrtPostFeedSelection & {
  feedType: PostFeedType;
  kind: MeinOrtFeedKind;
  newsFilter: ReturnType<typeof useMeinOrtNewsFeed>['newsFilter'];
  setNewsFilter: ReturnType<typeof useMeinOrtNewsFeed>['setNewsFilter'];
};

export const useMeinOrtPostFeed = (
  activeTab: MeinOrtTab
): UseMeinOrtPostFeedResult => {
  const { kind, feedType } = POST_FEED_CONFIG[activeTab] ?? DEFAULT_FEED_CONFIG;

  const sortBy = usePostSortStore((state) => state.sort);
  const defaultParams = useGetPostsParams(feedType);
  const meinOrtParams = useGetMeinOrtPostsParams(feedType);

  const news = useMeinOrtNewsFeed(kind === MeinOrtFeedKind.NEWS);
  const sagsUns = useGetSagsUnsPosts(
    { count: SIZE, startTime: LAST_100_DAYS, sortBy },
    kind === MeinOrtFeedKind.SAGS_UNS
  );
  const meinOrt = useGetMeinOrtPosts(
    meinOrtParams,
    kind === MeinOrtFeedKind.MEIN_ORT
  );

  const feedByKind: Record<MeinOrtFeedKind, MeinOrtPostFeedSelection> = {
    [MeinOrtFeedKind.NONE]: { params: defaultParams, query: meinOrt },
    [MeinOrtFeedKind.MEIN_ORT]: { params: defaultParams, query: meinOrt },
    [MeinOrtFeedKind.NEWS]: { params: news.params, query: news.query },
    [MeinOrtFeedKind.SAGS_UNS]: { params: defaultParams, query: sagsUns }
  };

  const feed = feedByKind[kind];

  return {
    feedType,
    kind,
    newsFilter: news.newsFilter,
    params: feed.params,
    query: feed.query,
    setNewsFilter: news.setNewsFilter
  };
};
